import { format } from "date-fns";
import { StatusTagType } from "@components/StatusTagGeneric/StatusTagGeneric";
import {
  financialStatusAcronym,
  financialStatusDescriptionPTBR,
  freighTypesEnum,
} from "../models/ENUM_MY_ORDERS";
import { PixApiResponse } from "../models/IPixPayment";
import { PixPayload } from "../models/IPixPayment";

export const getFinancialStatusPTBR = (status: string) => {
  switch (status) {
    case financialStatusAcronym.BL_BLOQUEADO: {
      return financialStatusDescriptionPTBR.BLOQUEADO;
    }
    case financialStatusAcronym.LIB_LIBERADO: {
      return financialStatusDescriptionPTBR.LIBERADO;
    }
    case financialStatusAcronym.FT_FATURADO: {
      return financialStatusDescriptionPTBR.FATURADO;
    }
    case financialStatusAcronym.DEV_DEVOLUCAO: {
      return financialStatusDescriptionPTBR.DEVOLUCAO;
    }
    default: {
      return "Indefinido";
    }
  }
};

export const getFinancialStatus = (statusFinancial: string) => {
  switch (statusFinancial) {
    case financialStatusAcronym.BL_BLOQUEADO: {
      return StatusTagType.ERROR;
    }
    case financialStatusAcronym.LIB_LIBERADO: {
      return StatusTagType.WARNING;
    }
    case financialStatusAcronym.FT_FATURADO: {
      return StatusTagType.SUCCESS;
    }
    case financialStatusAcronym.DEV_DEVOLUCAO: {
      return StatusTagType.NONE;
    }
    default: {
      return StatusTagType.NONE;
    }
  }
};

export const isBlockCalendarDelivery = (
  freightType: string,
  orderStatus?: string
): boolean => {
  if (orderStatus === "LIB") {
    return false;
  }

  // Para ordens bloqueadas, permita a alteração a qualquer momento.
  if (orderStatus === "BL") {
    return false;
  }

  if (orderStatus === "FT") {
    return true;
  }

  const currentHour = new Date().getHours();
  const isCIFBlocked = freightType === freighTypesEnum.CIF && currentHour > 13;
  const isFOBBlocked = freightType === freighTypesEnum.FOB && currentHour > 14;

  return isCIFBlocked || isFOBBlocked;
};

export const getStatusColorStatusFinancial = (status: string): string => {
  switch (status) {
    case "BL":
      return "#D91F05";
    case "LIB":
      return "#FFB800";
    case "FT":
      return "#0BB873";
    case "carregamento":
    case "aguardando carregamento":
    case "carregamento finalizado":
    case "carregamento cancelado":
    case "DEV":
      return "#3E3D3D";
    default:
      return "#3E3D3D";
  }
};

/**
 * Retorna o texto de exibição para o criador do pedido.
 * Prioriza nome > email > fallback "-".
 */
export function getCreatorDisplayName(
  creatorName?: string | null,
  creatorEmail?: string | null
): string {
  return creatorName || creatorEmail || "-";
}

/**
 * Mapeia o statusAtem (BL, LIB, FT, DEV) para o formato esperado pelo modal de pagamento Pix.
 */
export function mapOrderStatus(
  statusAtem?: string
): "BLOQUEADO" | "LIBERADO" | "FATURADO" {
  switch (statusAtem) {
    case "FT":
      return "FATURADO";
    case "LIB":
    case "D":
    case "A":
      return "LIBERADO";
    default:
      return "BLOQUEADO";
  }
}

/**
 * Formata a data de entrega para exibição, tratando a data "vazia" ("0000-00-00")
 * retornada pela API sem quebrar em runtime quando o valor não é uma instância de Date válida.
 */
export function formatDeliveryDate(date: Date | string): string {
  if (String(date) === "0000-00-00") return "";
  return format(new Date(date), "dd/MM/yyyy");
}

export function mapPixApiToPayload(data: PixApiResponse[]): PixPayload[] {
  return data.map((item) => ({
    id: item.UUID,
    copiaECola: item.qrcode,
    status: item.isPaid ? "pago" : "pendente",
    valor: Number(item.netValue),
    uuid: item.UUID,
    main: item.main,
    createdAt: item.createdAt ?? undefined,
    transactionId: item.transactionId ?? undefined,
    receiverName: item.receiverName ?? undefined,
    bankName: item.bankName ?? undefined,
    issueDate: item.issueDate ? formatPixDate(item.issueDate) : undefined,
    paymentStatus: item.paymentStatus ?? undefined,
    paymentMethod: item.paymentMethod ?? undefined,
  }));
}

function formatPixDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    const parts = new Intl.DateTimeFormat("pt-BR", {
      timeZone: "America/Manaus",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);

    const get = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? "";

    return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get(
      "minute"
    )}`;
  } catch {
    return isoDate;
  }
}

/**
 * Regra única de "pago" para o Pix (SP-1294 / D4).
 *
 * Tolera os dois sinais de pagamento que o backend pode enviar, espelhando a
 * própria regra do backend (`isPaid === true || paymentStatus === "PAID"`):
 * - `paymentStatus === "PAID"`: cobre o cenário feature_pix_v2 ligada (Fase 2),
 *   quando o backend preenche o status.
 * - `status === "pago"`: derivado de `isPaid` em `mapPixApiToPayload`. Cobre os
 *   casos em que o backend não manda `paymentStatus` (Pix complementares e
 *   feature_pix_v2 desligada / Fase 1).
 *
 * Paliativo (Rota A): reconhece um Pix pago independentemente de qual sinal o
 * backend enviou, sem depender de correção no ms-sap.
 */
export function isPixPaid(payload: PixPayload): boolean {
  return payload.paymentStatus === "PAID" || payload.status === "pago";
}

/**
 * Pontua o quão "informativo" é um registro de Pix. Usado na deduplicação
 * para, entre duplicatas do mesmo UUID, manter o registro com sinal de
 * pagamento mais forte (D2).
 */
function pixPayloadInfoScore(payload: PixPayload): number {
  let score = 0;
  if (isPixPaid(payload)) score += 4;
  if (payload.transactionId) score += 2;
  if (payload.paymentStatus) score += 1;
  return score;
}

/**
 * Deduplica a lista de Pix por `uuid` (D2). A API retorna o mesmo Pix
 * repetido e, às vezes, a cópia secundária vem com campos nulos. Mantém,
 * para cada UUID, o registro mais informativo e preserva a flag `main`.
 */
export function dedupePixPayloads(payloads: PixPayload[]): PixPayload[] {
  const byUuid = new Map<string, PixPayload>();

  payloads.forEach((payload) => {
    const key = payload.uuid ?? payload.id;
    const current = byUuid.get(key);

    if (!current) {
      byUuid.set(key, payload);
      return;
    }

    const best =
      pixPayloadInfoScore(payload) > pixPayloadInfoScore(current)
        ? payload
        : current;

    // Preserva a flag main caso qualquer um dos registros a possua.
    byUuid.set(key, {
      ...best,
      main: best.main || current.main || payload.main,
    });
  });

  return Array.from(byUuid.values());
}

/**
 * Ordena os Pix para exibição em abas (D3): o Pix principal (`main`) primeiro,
 * depois os complementares por `createdAt` ascendente. Entradas sem
 * `createdAt` vão para o fim, preservando a ordem relativa.
 */
export function sortPixPayloadsForTabs(payloads: PixPayload[]): PixPayload[] {
  return [...payloads].sort((a, b) => {
    if (a.main !== b.main) return a.main ? -1 : 1;

    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : NaN;

    const aValid = !Number.isNaN(aTime);
    const bValid = !Number.isNaN(bTime);

    if (aValid && bValid) return aTime - bTime;
    if (aValid) return -1;
    if (bValid) return 1;
    return 0;
  });
}

/**
 * Rótulo da aba de um Pix a partir da sua posição (D3): "Pix 1", "Pix 2", …
 */
export function getPixTabLabel(index: number): string {
  return `Pix ${index + 1}`;
}

export interface PixAggregates {
  /** Valor total da ordem (soma dos valores dos Pix deduplicados). */
  total: number;
  /** Quantidade de Pix pagos (via isPixPaid). */
  paidCount: number;
  /** Quantidade total de Pix da ordem. */
  count: number;
}

/**
 * Deriva os agregados exibidos no cabeçalho consolidado (D5): valor total,
 * total de pagos ("X de Y pagos") e quantidade. Espera uma lista já
 * deduplicada.
 */
export function getPixAggregates(payloads: PixPayload[]): PixAggregates {
  return payloads.reduce<PixAggregates>(
    (acc, payload) => {
      acc.total += payload.valor;
      if (isPixPaid(payload)) acc.paidCount += 1;
      acc.count += 1;
      return acc;
    },
    { total: 0, paidCount: 0, count: 0 }
  );
}

/**
 * Prepara a lista de Pix para o modal: deduplica (D2) e ordena para abas (D3).
 */
export function preparePixPayloadsForModal(
  payloads: PixPayload[]
): PixPayload[] {
  return sortPixPayloadsForTabs(dedupePixPayloads(payloads));
}
